<template>
  <div>
    <button
      style="display: none"
      type="button"
      class="btn-open-modal-bulk-price"
      data-bs-toggle="modal"
      data-bs-target="#modalBulkPriceUpdate"
    ></button>
    <button
      style="display: none"
      type="button"
      class="btn-close-modal-bulk-price"
      data-bs-dismiss="modal"
      data-bs-target="#modalBulkPriceUpdate"
    ></button>

    <div
      class="modal fade"
      id="modalBulkPriceUpdate"
      tabindex="-1"
      aria-labelledby="modalBulkPriceUpdateLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog bpu-dialog">
        <div class="modal-content bpu">
          <!-- header -->
          <div class="bpu-header">
            <div>
              <h5 class="bpu-title" id="modalBulkPriceUpdateLabel">
                Bulk price update
              </h5>
              <p class="bpu-subtitle">
                {{
                  step === 1
                    ? "Change many prices at once. Nothing is saved until you review it."
                    : "Review every price before it changes. Untick anything you want to leave alone."
                }}
              </p>
            </div>
            <button
              type="button"
              class="bpu-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              @click="resetAll"
            >
              <i class="material-icons">close</i>
            </button>
          </div>

          <!-- steps -->
          <div class="bpu-steps">
            <span class="bpu-step" :class="{ 'is-active': step === 1, 'is-done': step === 2 }">
              <span class="bpu-step-dot">1</span> Set the change
            </span>
            <span class="bpu-step-line"></span>
            <span class="bpu-step" :class="{ 'is-active': step === 2 }">
              <span class="bpu-step-dot">2</span> Review &amp; apply
            </span>
          </div>

          <div class="bpu-body">
            <!-- ================= STEP 1 ================= -->
            <div v-if="step === 1">
              <!-- scope -->
              <section class="bpu-card">
                <h6 class="bpu-card-title">Apply to</h6>

                <div v-if="selectedItems.length" class="bpu-scope-static">
                  <i class="material-icons">check_circle</i>
                  <span
                    ><b>{{ selectedItems.length }}</b> selected item(s)</span
                  >
                </div>

                <div v-else-if="setId" class="bpu-choices">
                  <label class="bpu-choice" :class="{ 'is-on': scope === 'set' }">
                    <input type="radio" value="set" v-model="scope" />
                    <span class="bpu-choice-body">
                      <span class="bpu-choice-title">Whole menu set</span>
                      <span class="bpu-choice-sub">{{ menuSetName || "Current menu set" }}</span>
                    </span>
                  </label>
                  <label
                    v-if="categoryId"
                    class="bpu-choice"
                    :class="{ 'is-on': scope === 'category' }"
                  >
                    <input type="radio" value="category" v-model="scope" />
                    <span class="bpu-choice-body">
                      <span class="bpu-choice-title">One category only</span>
                      <span class="bpu-choice-sub">{{ categoryName || "Category currently open" }}</span>
                    </span>
                  </label>
                </div>

                <p v-else class="bpu-warning">
                  <i class="material-icons">error_outline</i>
                  Tick the items to change, or open a menu set first.
                </p>
              </section>

              <!-- how long it lasts (M892) -->
              <section class="bpu-card">
                <h6 class="bpu-card-title">How long</h6>

                <div class="bpu-segment" role="group" aria-label="How long the change lasts">
                  <button
                    type="button"
                    class="bpu-segment-btn"
                    :class="{ 'is-on': !isTemporary }"
                    @click="setChangeType('permanent')"
                  >
                    <i class="material-icons">all_inclusive</i> Permanent
                  </button>
                  <button
                    type="button"
                    class="bpu-segment-btn"
                    :class="{ 'is-on': isTemporary }"
                    @click="setChangeType('temporary')"
                  >
                    <i class="material-icons">schedule</i> Temporary
                  </button>
                </div>

                <div v-if="isTemporary">
                  <div class="bpu-row">
                    <div class="bpu-field">
                      <span class="bpu-label">Starts</span>
                      <Datepicker
                        class="bpu-date"
                        v-model="form.start_date"
                        :format="dateLabel"
                        :min-date="now"
                        :start-date="now"
                        :start-time="startTime"
                        :is-24="true"
                        placeholder="Now"
                      />
                    </div>
                    <div class="bpu-field">
                      <span class="bpu-label">Ends</span>
                      <Datepicker
                        class="bpu-date"
                        v-model="form.end_date"
                        :format="dateLabel"
                        :min-date="minEndDate"
                        :start-date="minEndDate"
                        :start-time="startTime"
                        :is-24="true"
                        placeholder="Pick a date and time"
                      />
                    </div>
                  </div>
                  <p class="bpu-hint">{{ temporaryHint }}</p>
                  <p class="bpu-hint">{{ shopClockHint }}</p>
                </div>
                <p v-else class="bpu-hint">
                  The new prices stay until somebody changes them again.
                </p>
              </section>

              <!-- the change -->
              <section class="bpu-card">
                <h6 class="bpu-card-title">The change</h6>

                <div class="bpu-segment" role="group" aria-label="Direction">
                  <button
                    type="button"
                    v-for="option in directions"
                    :key="option.value"
                    class="bpu-segment-btn"
                    :class="{ 'is-on': form.direction === option.value }"
                    @click="form.direction = option.value"
                  >
                    <i class="material-icons">{{ option.icon }}</i>
                    {{ option.label }}
                  </button>
                </div>

                <div class="bpu-row">
                  <div class="bpu-field">
                    <label class="bpu-label" for="bpuValue">
                      {{ form.direction === "set" ? "New price" : "Amount to change by" }}
                    </label>
                    <div class="bpu-input-wrap">
                      <span class="bpu-adorn" v-if="unitSymbol === '$'">$</span>
                      <input
                        id="bpuValue"
                        class="bpu-input"
                        :class="{ 'has-prefix': unitSymbol === '$', 'has-suffix': unitSymbol === '%' }"
                        type="number"
                        step="0.01"
                        min="0"
                        inputmode="decimal"
                        v-model="form.operation_value"
                        placeholder="0.00"
                      />
                      <span class="bpu-adorn is-suffix" v-if="unitSymbol === '%'">%</span>
                    </div>
                  </div>

                  <div class="bpu-field" v-if="form.direction !== 'set'">
                    <span class="bpu-label">Measured in</span>
                    <div class="bpu-segment is-compact">
                      <button
                        type="button"
                        class="bpu-segment-btn"
                        :class="{ 'is-on': form.unit === 'percent' }"
                        @click="form.unit = 'percent'"
                      >
                        Percent
                      </button>
                      <button
                        type="button"
                        class="bpu-segment-btn"
                        :class="{ 'is-on': form.unit === 'amount' }"
                        @click="form.unit = 'amount'"
                      >
                        Fixed amount
                      </button>
                    </div>
                  </div>

                  <div class="bpu-field">
                    <label class="bpu-label" for="bpuRounding">Round the result</label>
                    <select id="bpuRounding" class="bpu-input bpu-select" v-model="form.rounding">
                      <option value="none">Don't round (2 decimals)</option>
                      <option value="0.05">Nearest 0.05</option>
                      <option value="0.10">Nearest 0.10</option>
                      <option value="1.00">Nearest 1.00</option>
                      <option value="0.90">Make it end in .90</option>
                    </select>
                  </div>
                </div>

                <p class="bpu-example" v-if="examplePreview">
                  <i class="material-icons">calculate</i>
                  For example, a price of <b>{{ money(10) }}</b> becomes
                  <b>{{ examplePreview }}</b>
                </p>
              </section>

              <!-- which prices -->
              <section class="bpu-card">
                <div class="bpu-card-head">
                  <div>
                    <h6 class="bpu-card-title">Which prices to change</h6>
                    <p class="bpu-card-sub">Anything not selected is left exactly as it is.</p>
                  </div>
                  <button type="button" class="bpu-link" @click="toggleAllPriceKeys">
                    {{ allPriceKeysOn ? "Clear all" : "Select all" }}
                  </button>
                </div>

                <div class="bpu-chips">
                  <button
                    type="button"
                    class="bpu-chip"
                    :class="{ 'is-on': form.targets.base }"
                    @click="form.targets.base = !form.targets.base"
                  >
                    <i class="material-icons">{{ form.targets.base ? "check" : "add" }}</i>
                    Base price
                  </button>
                  <button
                    type="button"
                    class="bpu-chip"
                    v-for="key in priceKeys"
                    :key="key.value"
                    :class="{ 'is-on': form.targets.price_keys.indexOf(key.value) !== -1 }"
                    @click="togglePriceKey(key.value)"
                  >
                    <i class="material-icons">
                      {{ form.targets.price_keys.indexOf(key.value) !== -1 ? "check" : "add" }}
                    </i>
                    {{ key.label }}
                  </button>
                </div>
              </section>

              <!-- delivery platforms -->
              <section class="bpu-card">
                <h6 class="bpu-card-title">Delivery platform prices</h6>
                <p class="bpu-card-sub">
                  Off by default. These carry their own markup, so changing them
                  changes what the platform charges your customers.
                </p>
                <div class="bpu-chips">
                  <button
                    type="button"
                    class="bpu-chip"
                    v-for="platform in platforms"
                    :key="platform"
                    :class="{ 'is-on': form.targets.platforms.indexOf(platform) !== -1 }"
                    @click="togglePlatform(platform)"
                  >
                    <i class="material-icons">
                      {{ form.targets.platforms.indexOf(platform) !== -1 ? "check" : "add" }}
                    </i>
                    {{ platform }}
                  </button>
                </div>
              </section>

              <!-- reason -->
              <section class="bpu-card">
                <label class="bpu-label" for="bpuReason">Reason <span class="bpu-optional">(optional)</span></label>
                <input
                  id="bpuReason"
                  class="bpu-input"
                  type="text"
                  maxlength="500"
                  v-model="form.note"
                  placeholder="e.g. Supplier cost increase, Jan 2026"
                />
                <p class="bpu-hint">Saved with the change history so the team knows why.</p>
              </section>
            </div>

            <!-- ================= STEP 2 ================= -->
            <div v-if="step === 2">
              <p class="bpu-note" v-if="isTemporary">
                <i class="material-icons">schedule</i>
                {{ temporaryHint }}
              </p>

              <div class="bpu-stats">
                <div class="bpu-stat">
                  <span class="bpu-stat-value">{{ includedItemCount }}</span>
                  <span class="bpu-stat-label">items change</span>
                </div>
                <div class="bpu-stat">
                  <span class="bpu-stat-value">{{ includedLineCount }}</span>
                  <span class="bpu-stat-label">prices change</span>
                </div>
                <div class="bpu-stat is-muted" v-if="excludedKeys.length">
                  <span class="bpu-stat-value">{{ excludedKeys.length }}</span>
                  <span class="bpu-stat-label">lines unticked</span>
                </div>
                <div class="bpu-stat is-muted" v-if="summary.total_skipped">
                  <span class="bpu-stat-value">{{ summary.total_skipped }}</span>
                  <span class="bpu-stat-label">items skipped</span>
                </div>
              </div>

              <p class="bpu-note" v-if="summary.preview_truncated">
                <i class="material-icons">info_outline</i>
                The table lists the first {{ summary.preview_limit }} items. All
                {{ summary.total_items_changed }} matching items are part of this run.
              </p>

              <div class="bpu-table-wrap">
                <table class="bpu-table">
                  <thead>
                    <tr>
                      <th class="bpu-col-check">
                        <label class="bpu-box">
                          <input
                            type="checkbox"
                            :checked="allLinesIncluded"
                            @change="toggleAllLines($event)"
                          />
                          <span></span>
                        </label>
                      </th>
                      <th>Item</th>
                      <th>Price</th>
                      <th class="bpu-num">Before</th>
                      <th class="bpu-num">After</th>
                      <th class="bpu-num">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="item in previewItems" :key="item.item_id">
                      <tr
                        v-for="(line, idx) in item.lines"
                        :key="item.item_id + '-' + idx"
                        :class="{
                          'is-off': isItemExcluded(item),
                          'is-group-start': idx === 0,
                        }"
                      >
                        <!-- one tick per item: it covers every price row of that
                             item, so two rows of the same dish can never look
                             like two different dishes -->
                        <td
                          class="bpu-col-check"
                          v-if="idx === 0"
                          :rowspan="item.lines.length"
                        >
                          <label class="bpu-box">
                            <input
                              type="checkbox"
                              :checked="!isItemExcluded(item)"
                              @change="toggleItem(item)"
                            />
                            <span></span>
                          </label>
                        </td>
                        <td
                          class="bpu-item-cell"
                          v-if="idx === 0"
                          :rowspan="item.lines.length"
                        >
                          <span class="bpu-item-name">{{ item.item_name }}</span>
                          <span class="bpu-item-no">{{ item.item_no }}</span>
                        </td>
                        <td>
                          <span class="bpu-tag">{{ line.price_label }}</span>
                        </td>
                        <td class="bpu-num bpu-old">{{ money(line.old_value) }}</td>
                        <td class="bpu-num bpu-new">{{ money(line.new_value) }}</td>
                        <td class="bpu-num" :class="line.diff >= 0 ? 'bpu-up' : 'bpu-down'">
                          {{ line.diff >= 0 ? "+" : "−" }}{{ money(Math.abs(line.diff)) }}
                        </td>
                      </tr>
                    </template>
                    <tr v-if="!previewItems.length">
                      <td colspan="6" class="bpu-empty">
                        Nothing would change with these settings.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="bpu-skipped" v-if="skipped.length">
                <button type="button" class="bpu-link" @click="showSkipped = !showSkipped">
                  {{ showSkipped ? "Hide" : "Show" }} the {{ skipped.length }} skipped item(s)
                </button>
                <ul v-if="showSkipped">
                  <li v-for="(row, idx) in skipped.slice(0, 50)" :key="idx">
                    <b>{{ row.item_no }}</b> {{ row.item_name }} —
                    <span>{{ row.reason_text }}</span>
                  </li>
                  <li v-if="skipped.length > 50">…and {{ skipped.length - 50 }} more</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- footer -->
          <div class="bpu-footer">
            <button
              v-if="step === 2"
              type="button"
              class="bpu-btn bpu-btn-ghost"
              @click="goBack"
            >
              <i class="material-icons">arrow_back</i> Back
            </button>
            <span class="bpu-spacer"></span>
            <button
              type="button"
              class="bpu-btn bpu-btn-ghost"
              data-bs-dismiss="modal"
              @click="resetAll"
            >
              Cancel
            </button>
            <button
              v-if="step === 1"
              type="button"
              class="bpu-btn bpu-btn-primary"
              :disabled="processing"
              @click="doPreview"
            >
              {{ processing ? "Checking…" : "Preview changes" }}
            </button>
            <button
              v-if="step === 2"
              type="button"
              class="bpu-btn bpu-btn-primary"
              :disabled="processing || !includedLineCount"
              @click="doApply"
            >
              {{ applyLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { SHOP_TIMEZONE } from "@/config/utils";
import ItemService from "../services/product/item.service";

export default {
  name: "ModalBulkPriceUpdate",
  components: {
    Datepicker,
  },
  props: {
    // Items ticked on the list. Empty means "everything in the scope below".
    selectedItems: {
      type: Array,
      default: () => [],
    },
    setId: {
      type: [Number, String],
      default: 0,
    },
    searchKeyword: {
      type: String,
      default: "",
    },
    // Category currently open on the Menu Set screen, so the merchant can
    // narrow a whole-menu-set change down to what they are looking at.
    categoryId: {
      type: [Number, String],
      default: 0,
    },
    categoryName: {
      type: String,
      default: "",
    },
    menuSetName: {
      type: String,
      default: "",
    },
  },
  emits: ["applied", "error"],
  data() {
    return {
      step: 1,
      processing: false,
      showSkipped: false,
      previewItems: [],
      skipped: [],
      summary: {},
      scope: "set",
      excludedKeys: [],
      directions: [
        { value: "increase", label: "Increase", icon: "trending_up" },
        { value: "decrease", label: "Decrease", icon: "trending_down" },
        { value: "set", label: "Set price", icon: "sell" },
      ],
      priceKeys: Array.from({ length: 9 }, (_, i) => ({
        value: "price_" + (i + 1),
        label: "Price " + (i + 1),
      })),
      platforms: ["Grab", "Foodpanda", "Deliveroo"],
      // Shop-clock "now", refreshed when the modal opens; the pickers use it as
      // their minimum.
      nowKey: null,
      form: this.emptyForm(),
    };
  },
  computed: {
    unitSymbol() {
      if (this.form.direction === "set") {
        return "$";
      }

      return this.form.unit === "percent" ? "%" : "$";
    },
    allPriceKeysOn() {
      return this.form.targets.base && this.form.targets.price_keys.length === this.priceKeys.length;
    },
    /** Worked example on a $10 price, so the effect is obvious before previewing. */
    examplePreview() {
      const value = Number(this.form.operation_value);

      if (!this.form.operation_value || isNaN(value)) {
        return "";
      }

      let result;
      if (this.form.direction === "set") {
        result = Math.abs(value);
      } else {
        const signed = this.form.direction === "decrease" ? -Math.abs(value) : Math.abs(value);
        result = this.form.unit === "percent" ? 10 * (1 + signed / 100) : 10 + signed;
      }

      if (result < 0) {
        return "0.00 (the result would be negative — those prices are skipped)";
      }

      return this.money(this.roundLikeServer(result));
    },
    /** Operation + signed value the API expects. */
    operationPayload() {
      const value = Math.abs(Number(this.form.operation_value));

      if (this.form.direction === "set") {
        return { operation: "absolute", operation_value: value };
      }

      const signed = this.form.direction === "decrease" ? -value : value;

      return {
        operation: this.form.unit === "percent" ? "percent" : "amount",
        operation_value: signed,
      };
    },
    /**
     * Counts come off the server summary, not off previewItems: the table is
     * capped at PREVIEW_ITEM_LIMIT rows while the run covers every matching
     * item, so counting the visible rows would understate what Apply does.
     */
    includedLineCount() {
      return Math.max(0, (this.summary.total_lines || 0) - this.excludedKeys.length);
    },
    includedItemCount() {
      return Math.max(0, (this.summary.total_items_changed || 0) - this.fullyExcludedItemCount);
    },
    /** Items whose every line was unticked - they drop out of the run entirely. */
    fullyExcludedItemCount() {
      return this.previewItems.filter((item) => {
        return item.lines.length > 0 && item.lines.every((line) => this.isExcluded(line));
      }).length;
    },
    allLinesIncluded() {
      return this.excludedKeys.length === 0;
    },
    isTemporary() {
      return this.form.change_type === "temporary";
    },
    /**
     * Earliest moment either picker will offer. Read once when the modal opens
     * (nowKey changes on open) rather than on every keystroke, so the minimum
     * does not creep forward while the merchant is choosing.
     */
    now() {
      return this.nowKey || this.shopNow();
    },
    minEndDate() {
      return this.form.start_date ? new Date(this.form.start_date) : this.now;
    },
    /** A start time later than now means the run is booked, not applied. */
    isScheduled() {
      return this.isTemporary && !!this.form.start_date && this.form.start_date > this.shopNow();
    },
    /**
     * Time a picker lands on when the merchant clicks a day: the shop clock,
     * not the laptop's. Without this a browser an hour behind opens on a time
     * the minimum already rejects, and Select stays dead with no explanation.
     */
    startTime() {
      const now = this.now;

      return { hours: now.getHours(), minutes: now.getMinutes() };
    },
    /** Spelt out under the pickers, so a laptop on another clock is obvious. */
    shopClockHint() {
      return "Times follow the shop clock (" + SHOP_TIMEZONE.split("/")[1].replace("_", " ") +
        ") — it is " + this.dateLabel(this.shopNow()) + " there now.";
    },
    temporaryHint() {
      if (!this.form.end_date) {
        return "Pick when the new prices should stop. They go back on their own at that moment.";
      }

      const from = this.form.start_date ? this.longDate(this.form.start_date) : "now";

      return (
        "Prices change " + from + " and go back to what they are now on " +
        this.longDate(this.form.end_date) + "."
      );
    },
    applyLabel() {
      if (this.processing) {
        return this.isScheduled ? "Scheduling…" : "Applying…";
      }

      if (this.isScheduled) {
        return "Schedule for " + this.shortDate(this.form.start_date);
      }

      return "Apply to " + this.includedItemCount + " item(s)";
    },
  },
  methods: {
    emptyForm() {
      return {
        direction: "increase",
        unit: "percent",
        operation_value: "",
        rounding: "none",
        // M892 - permanent is the M886 behaviour; temporary runs between two
        // days and is put back on its own.
        change_type: "permanent",
        start_date: null,
        end_date: null,
        note: "",
        targets: {
          base: true,
          price_keys: ["price_1"],
          platforms: [],
        },
      };
    },
    money(value) {
      return Number(value || 0).toFixed(2);
    },
    /**
     * "Now" on the shop clock, carried in a plain Date whose local parts are
     * the Singapore wall clock. Everything else in this component reads those
     * parts, so what the merchant picks is exactly what the API stores - a
     * browser running on another timezone no longer books a time the API
     * considers past.
     */
    shopNow() {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: SHOP_TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).formatToParts(new Date());

      const get = (type) => Number(parts.find((part) => part.type === type).value);

      // hour comes back as 24 at midnight in some engines
      return new Date(
        get("year"), get("month") - 1, get("day"),
        get("hour") % 24, get("minute"), get("second")
      );
    },
    setChangeType(type) {
      this.form.change_type = type;

      if (type !== "temporary") {
        this.form.start_date = null;
        this.form.end_date = null;
      }
    },
    /**
     * Y-m-d H:i:00 built from the local parts. The merchant picks to the
     * minute, so the seconds are always sent as zero; toISOString() would
     * convert to UTC first and shift an evening booking onto the wrong day.
     */
    toYmdHis(date) {
      if (!date) {
        return null;
      }

      const value = new Date(date);
      const pad = (part) => String(part).padStart(2, "0");

      return (
        value.getFullYear() + "-" + pad(value.getMonth() + 1) + "-" + pad(value.getDate()) +
        " " + pad(value.getHours()) + ":" + pad(value.getMinutes()) + ":00"
      );
    },
    /** What the picker itself shows once a moment is chosen. */
    dateLabel(date) {
      const value = new Date(date);
      const pad = (part) => String(part).padStart(2, "0");

      return (
        pad(value.getDate()) + "/" + pad(value.getMonth() + 1) + "/" + value.getFullYear() +
        " " + pad(value.getHours()) + ":" + pad(value.getMinutes())
      );
    },
    longDate(date) {
      return new Date(date).toLocaleString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    shortDate(date) {
      return new Date(date).toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    /** Mirrors BulkItemPriceService::applyRounding for the example line only. */
    roundLikeServer(value) {
      switch (this.form.rounding) {
        case "0.05":
          return Math.round(value / 0.05) * 0.05;
        case "0.10":
          return Math.round(value / 0.1) * 0.1;
        case "1.00":
          return Math.round(value);
        case "0.90":
          return Math.max(0, Math.floor(value)) + 0.9;
        default:
          return value;
      }
    },
    togglePriceKey(key) {
      const index = this.form.targets.price_keys.indexOf(key);

      if (index === -1) {
        this.form.targets.price_keys.push(key);
      } else {
        this.form.targets.price_keys.splice(index, 1);
      }
    },
    togglePlatform(platform) {
      const index = this.form.targets.platforms.indexOf(platform);

      if (index === -1) {
        this.form.targets.platforms.push(platform);
      } else {
        this.form.targets.platforms.splice(index, 1);
      }
    },
    toggleAllPriceKeys() {
      if (this.allPriceKeysOn) {
        this.form.targets.base = false;
        this.form.targets.price_keys = [];
        return;
      }

      this.form.targets.base = true;
      this.form.targets.price_keys = this.priceKeys.map((key) => key.value);
    },
    payload() {
      const data = {
        operation: this.operationPayload.operation,
        operation_value: this.operationPayload.operation_value,
        rounding: this.form.rounding,
        change_type: this.isTemporary ? 2 : 1,
        start_date: this.isTemporary ? this.toYmdHis(this.form.start_date) : null,
        end_date: this.isTemporary ? this.toYmdHis(this.form.end_date) : null,
        note: this.form.note,
        excluded_lines: this.excludedKeys,
        targets: {
          base: this.form.targets.base,
          price_keys: this.form.targets.price_keys,
          platforms: this.form.targets.platforms,
        },
      };

      if (this.selectedItems.length) {
        data.item_ids = this.selectedItems;
      } else {
        data.set_id = this.setId || 0;
        data.filter = { search_keyword: this.searchKeyword };

        if (this.scope === "category" && this.categoryId) {
          data.filter.category_id = this.categoryId;
        }
      }

      return data;
    },
    isExcluded(line) {
      return this.excludedKeys.indexOf(line.line_key) !== -1;
    },
    isItemExcluded(item) {
      return item.lines.every((line) => this.isExcluded(line));
    },
    /** Ticking an item covers all of its price rows at once. */
    toggleItem(item) {
      if (this.isItemExcluded(item)) {
        this.excludedKeys = this.excludedKeys.filter((key) => {
          return !item.lines.some((line) => line.line_key === key);
        });
        return;
      }

      item.lines.forEach((line) => {
        if (!this.isExcluded(line)) {
          this.excludedKeys.push(line.line_key);
        }
      });
    },
    toggleAllLines(event) {
      if (event.target.checked) {
        this.excludedKeys = [];
        return;
      }

      this.excludedKeys = this.previewItems.reduce((keys, item) => {
        return keys.concat(item.lines.map((line) => line.line_key));
      }, []);
    },
    doPreview() {
      if (this.form.operation_value === "" || isNaN(Number(this.form.operation_value))) {
        this.$emit("error", "Enter a value first.");
        return;
      }

      if (!this.selectedItems.length && !this.setId) {
        this.$emit("error", "Tick the items to change, or open a menu set first.");
        return;
      }

      if (
        !this.form.targets.base &&
        !this.form.targets.price_keys.length &&
        !this.form.targets.platforms.length
      ) {
        this.$emit("error", "Choose at least one price to change.");
        return;
      }

      if (this.isTemporary) {
        if (!this.form.end_date) {
          this.$emit("error", "Choose when the temporary price should end.");
          return;
        }

        const startsAt = this.form.start_date || this.shopNow();

        if (this.form.end_date <= startsAt) {
          this.$emit("error", "The end time must be after the start time.");
          return;
        }

        if (this.form.end_date <= this.shopNow()) {
          this.$emit("error", "The end time has already passed.");
          return;
        }
      }

      this.processing = true;
      ItemService.bulkPreview(this.payload()).then(
        (response) => {
          this.processing = false;
          if (response.data.result.isSuccess) {
            this.previewItems = response.data.data.items || [];
            this.skipped = response.data.data.skipped || [];
            this.summary = response.data.data.summary || {};
            this.excludedKeys = [];
            this.showSkipped = false;
            this.step = 2;
            this.scrollBodyTop();
          } else {
            this.$emit("error", response.data.result.message);
          }
        },
        (error) => {
          this.processing = false;
          this.$emit("error", error.toString());
        }
      );
    },
    doApply() {
      this.processing = true;
      ItemService.bulkApply(this.payload()).then(
        (response) => {
          this.processing = false;
          if (response.data.result.isSuccess) {
            const applied = response.data.data.summary || {};
            this.close();
            this.$emit("applied", applied);
          } else {
            this.$emit("error", response.data.result.message);
          }
        },
        (error) => {
          this.processing = false;
          this.$emit("error", error.toString());
        }
      );
    },
    goBack() {
      this.step = 1;
      this.scrollBodyTop();
    },
    scrollBodyTop() {
      this.$nextTick(() => {
        const body = document.querySelector("#modalBulkPriceUpdate .bpu-body");

        if (body) {
          body.scrollTop = 0;
        }
      });
    },
    open() {
      this.nowKey = this.shopNow();
      this.resetAll();
      $(".btn-open-modal-bulk-price").click();
    },
    close() {
      $(".btn-close-modal-bulk-price").click();
    },
    resetAll() {
      this.step = 1;
      this.scope = "set";
      this.processing = false;
      this.showSkipped = false;
      this.previewItems = [];
      this.skipped = [];
      this.summary = {};
      this.excludedKeys = [];
      this.form = this.emptyForm();
    },
  },
};
</script>

<style scoped>
/*
 * Self-contained styling. The app theme sets `.form-control { border: none }`
 * and paints an underline with a background-image, which meant every border we
 * added disappeared on hover/focus. Nothing here reuses those class names, so
 * the theme cannot reach in.
 */
.bpu {
  --bpu-ink: #1c2530;
  --bpu-ink-soft: #5b6672;
  --bpu-line: #dfe3e8;
  --bpu-line-strong: #b9c0c8;
  --bpu-bg-soft: #f6f8fa;
  --bpu-brand: #1871bd;
  --bpu-brand-soft: #e8f2fb;
  --bpu-up: #1a7f4b;
  --bpu-down: #b3261e;

  border: 0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 18px 48px rgba(16, 24, 32, 0.18);
  color: var(--bpu-ink);
  font-size: 14px;
}

.bpu-dialog {
  max-width: min(1040px, 94vw);
  margin: 1.5rem auto;
}

/* ---------- header ---------- */
.bpu-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--bpu-line);
}

.bpu-title {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--bpu-ink);
}

.bpu-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--bpu-ink-soft);
}

.bpu-close {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--bpu-ink-soft);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.bpu-close:hover {
  background: var(--bpu-bg-soft);
  color: var(--bpu-ink);
}

.bpu-close .material-icons {
  font-size: 20px;
}

/* ---------- step rail ---------- */
.bpu-steps {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  background: var(--bpu-bg-soft);
  border-bottom: 1px solid var(--bpu-line);
  font-size: 13px;
  color: var(--bpu-ink-soft);
}

.bpu-step {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.bpu-step.is-active {
  color: var(--bpu-ink);
  font-weight: 600;
}

.bpu-step-dot {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid var(--bpu-line-strong);
  font-size: 12px;
  font-weight: 600;
}

.bpu-step.is-active .bpu-step-dot {
  background: var(--bpu-brand);
  border-color: var(--bpu-brand);
  color: #fff;
}

.bpu-step.is-done .bpu-step-dot {
  background: var(--bpu-brand-soft);
  border-color: var(--bpu-brand);
  color: var(--bpu-brand);
}

.bpu-step-line {
  flex: 0 0 40px;
  height: 1px;
  background: var(--bpu-line-strong);
}

/* ---------- body ---------- */
.bpu-body {
  padding: 20px 24px;
  max-height: min(62vh, 640px);
  overflow-y: auto;
}

.bpu-card {
  border: 1px solid var(--bpu-line);
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 14px;
}

.bpu-card:last-child {
  margin-bottom: 0;
}

.bpu-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.bpu-card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--bpu-ink);
}

.bpu-card-sub {
  margin: 4px 0 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--bpu-ink-soft);
}

/* ---------- scope ---------- */
.bpu-scope-static {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  color: var(--bpu-ink);
}

.bpu-scope-static .material-icons {
  font-size: 18px;
  color: var(--bpu-down);
}

.bpu-choices {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.bpu-choice {
  flex: 1 1 240px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--bpu-line);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  margin: 0;
}

.bpu-choice:hover {
  border-color: var(--bpu-line-strong);
  background: var(--bpu-bg-soft);
}

.bpu-choice.is-on {
  border-color: var(--bpu-brand);
  background: var(--bpu-brand-soft);
}

.bpu-choice input {
  margin-top: 2px;
  accent-color: var(--bpu-brand);
  width: 16px;
  height: 16px;
  flex: none;
}

.bpu-choice-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bpu-choice-title {
  font-weight: 600;
}

.bpu-choice-sub {
  font-size: 12.5px;
  color: var(--bpu-ink-soft);
}

.bpu-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 0 0;
  color: #b3261e;
  font-size: 13px;
}

.bpu-warning .material-icons {
  font-size: 18px;
}

/* ---------- segmented control ---------- */
.bpu-segment {
  display: inline-flex;
  padding: 4px;
  gap: 4px;
  background: var(--bpu-bg-soft);
  border: 1px solid var(--bpu-line);
  border-radius: 10px;
  margin: 12px 0 4px;
}

.bpu-segment.is-compact {
  margin: 0;
  width: 100%;
  min-height: 42px;
  padding: 3px;
}

.bpu-segment.is-compact .bpu-segment-btn {
  min-height: 34px;
  padding: 0 16px;
}

.bpu-segment-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex: 1;
  padding: 8px 16px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: var(--bpu-ink-soft);
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
}

.bpu-segment-btn .material-icons {
  font-size: 18px;
}

.bpu-segment-btn:hover {
  color: var(--bpu-ink);
}

.bpu-segment-btn.is-on {
  background: #fff;
  color: var(--bpu-brand);
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(16, 24, 32, 0.12);
}

/* ---------- fields ---------- */
.bpu-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  margin-top: 12px;
}

.bpu-field {
  flex: 1 1 200px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.bpu-label {
  display: block;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: var(--bpu-ink);
}

.bpu-optional {
  font-weight: 400;
  color: var(--bpu-ink-soft);
}

.bpu-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 42px;
}

/*
 * Explicit border on every state. The old theme dropped it on hover, which is
 * what made the field look broken.
 */
.bpu-input {
  width: 100%;
  min-height: 42px;
  padding: 10px 14px;
  border: 1px solid var(--bpu-line-strong);
  border-radius: 10px;
  background: #fff;
  color: var(--bpu-ink);
  font-size: 14px;
  line-height: 1.4;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
}

.bpu-input::placeholder {
  color: #9aa4ae;
}

.bpu-input:hover {
  border-color: #8d96a0;
}

.bpu-input:focus {
  outline: none;
  border-color: var(--bpu-brand);
  box-shadow: 0 0 0 3px rgba(24, 113, 189, 0.16);
}

.bpu-input.has-prefix {
  padding-left: 30px;
}

.bpu-input.has-suffix {
  padding-right: 34px;
}

.bpu-select {
  padding-right: 36px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath fill='%235b6672' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  cursor: pointer;
}

.bpu-adorn {
  position: absolute;
  left: 14px;
  color: var(--bpu-ink-soft);
  font-size: 14px;
  pointer-events: none;
}

.bpu-adorn.is-suffix {
  left: auto;
  right: 14px;
}

.bpu-hint {
  margin: 6px 0 0;
  font-size: 12.5px;
  color: var(--bpu-ink-soft);
}

/* ---------- date pickers (M892) ---------- */
/* The picker renders its own input, so the card's look has to be pushed into
   the library's DOM with :deep() - a plain descendant selector no-ops here. */
.bpu-date :deep(.dp__input) {
  min-height: 42px;
  padding: 10px 14px 10px 40px;
  border: 1px solid var(--bpu-line-strong);
  border-radius: 10px;
  background: #fff;
  color: var(--bpu-ink);
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
}

.bpu-date :deep(.dp__input:hover) {
  border-color: var(--bpu-line-strong);
}

.bpu-date :deep(.dp__input:focus) {
  border-color: var(--bpu-brand);
  box-shadow: 0 0 0 3px var(--bpu-brand-soft);
  outline: none;
}

.bpu-date :deep(.dp__input_icon) {
  color: var(--bpu-ink-soft);
}

.bpu-example {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 14px 0 0;
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--bpu-brand-soft);
  color: #17507f;
  font-size: 13px;
}

.bpu-example .material-icons {
  font-size: 18px;
}

/* ---------- chips ---------- */
.bpu-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.bpu-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px 8px 10px;
  border: 1px solid var(--bpu-line-strong);
  border-radius: 999px;
  background: #fff;
  color: var(--bpu-ink-soft);
  font-size: 13.5px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.bpu-chip .material-icons {
  font-size: 16px;
}

.bpu-chip:hover {
  border-color: var(--bpu-brand);
  color: var(--bpu-ink);
}

.bpu-chip.is-on {
  background: var(--bpu-brand);
  border-color: var(--bpu-brand);
  color: #fff;
  font-weight: 500;
}

.bpu-link {
  border: 0;
  background: none;
  padding: 0;
  color: var(--bpu-brand);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  white-space: nowrap;
}

/* ---------- step 2 ---------- */
.bpu-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}

.bpu-stat {
  flex: 1 1 120px;
  padding: 12px 16px;
  border: 1px solid var(--bpu-line);
  border-radius: 12px;
  background: var(--bpu-brand-soft);
}

.bpu-stat.is-muted {
  background: var(--bpu-bg-soft);
}

.bpu-stat-value {
  display: block;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.2;
}

.bpu-stat-label {
  display: block;
  margin-top: 2px;
  font-size: 12.5px;
  color: var(--bpu-ink-soft);
}

.bpu-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  padding: 10px 14px;
  border-radius: 10px;
  background: var(--bpu-bg-soft);
  color: var(--bpu-ink-soft);
  font-size: 12.5px;
}

.bpu-note .material-icons {
  font-size: 18px;
}

.bpu-table-wrap {
  border: 1px solid var(--bpu-line);
  border-radius: 12px;
  overflow: auto;
  max-height: 46vh;
}

.bpu-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.bpu-table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 10px 14px;
  background: var(--bpu-bg-soft);
  border-bottom: 1px solid var(--bpu-line);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bpu-ink-soft);
  text-align: left;
  white-space: nowrap;
}

.bpu-table tbody td {
  padding: 9px 14px;
  border-bottom: 1px solid #f2f4f6;
  vertical-align: middle;
}

/* a heavier rule between items, a hairline between an item's own price rows */
.bpu-table tbody tr.is-group-start td {
  border-top: 1px solid var(--bpu-line);
}

.bpu-table tbody tr:first-child td {
  border-top: 0;
}

.bpu-table tbody td.bpu-col-check,
.bpu-table tbody td.bpu-item-cell {
  vertical-align: top;
  padding-top: 12px;
}

.bpu-table tbody tr:last-child td {
  border-bottom: 0;
}

.bpu-table tbody tr:hover {
  background: #fafbfc;
}

.bpu-table tbody tr.is-off .bpu-item-name,
.bpu-table tbody tr.is-off .bpu-item-no {
  text-decoration: line-through;
}

.bpu-table tbody tr.is-off td {
  color: #a5adb6;
  text-decoration: line-through;
}

.bpu-table tbody tr.is-off .bpu-tag {
  opacity: 0.5;
}

.bpu-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.bpu-col-check {
  width: 44px;
  text-align: center;
}

.bpu-item-cell {
  min-width: 220px;
}

.bpu-item-name {
  display: block;
  font-weight: 500;
  color: var(--bpu-ink);
}

.bpu-item-no {
  display: block;
  font-size: 11.5px;
  color: var(--bpu-ink-soft);
}

.bpu-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: var(--bpu-bg-soft);
  font-size: 12px;
  color: var(--bpu-ink-soft);
  white-space: nowrap;
}

.bpu-old {
  color: var(--bpu-ink-soft);
}

.bpu-new {
  font-weight: 600;
}

.bpu-up {
  color: var(--bpu-up);
}

.bpu-down {
  color: var(--bpu-down);
}

.bpu-empty {
  padding: 28px 14px !important;
  text-align: center;
  color: var(--bpu-ink-soft);
}

/* own checkbox: the theme paints untick-ed boxes invisible and ticked ones red */
.bpu-box {
  display: inline-flex;
  margin: 0;
  cursor: pointer;
}

.bpu-box input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.bpu-box span {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 1px solid var(--bpu-line-strong);
  border-radius: 5px;
  background: #fff;
  transition: all 0.12s ease;
}

.bpu-box:hover span {
  border-color: var(--bpu-brand);
}

.bpu-box input:checked + span {
  background: var(--bpu-brand);
  border-color: var(--bpu-brand);
}

.bpu-box input:checked + span::after {
  content: "";
  display: block;
  width: 4px;
  height: 9px;
  margin: 1px auto 0;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.bpu-box input:focus-visible + span {
  box-shadow: 0 0 0 3px rgba(24, 113, 189, 0.22);
}

.bpu-skipped {
  margin-top: 14px;
}

.bpu-skipped ul {
  margin: 10px 0 0;
  padding-left: 18px;
  font-size: 12.5px;
  color: var(--bpu-ink-soft);
  line-height: 1.7;
}

/* ---------- footer ---------- */
.bpu-footer {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--bpu-line);
  background: #fff;
}

.bpu-spacer {
  flex: 1;
}

.bpu-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 42px;
  padding: 0 20px;
  border-radius: 10px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
}

.bpu-btn .material-icons {
  font-size: 18px;
}

.bpu-btn-ghost {
  background: #fff;
  border-color: var(--bpu-line-strong);
  color: var(--bpu-ink);
}

.bpu-btn-ghost:hover {
  background: var(--bpu-bg-soft);
  border-color: #8d96a0;
}

.bpu-btn-primary {
  background: var(--bpu-brand);
  color: #fff;
}

.bpu-btn-primary:hover:not(:disabled) {
  background: #15619f;
}

.bpu-btn-primary:disabled {
  background: #b6cee2;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .bpu-header,
  .bpu-body,
  .bpu-footer,
  .bpu-steps {
    padding-left: 16px;
    padding-right: 16px;
  }

  .bpu-segment {
    width: 100%;
  }
}
</style>
